import { memo, useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { amber } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';

import { Icon } from '../../shared/Icon';
import { ElementTile } from './ElementTile';

import type { GameElementDto } from '../../hooks/gameElements';

import { formatDateRange } from '../utils/dates';

import type { ScheduledGiftOccurrence } from '../domain/giftSchedule';

interface GiftScheduleGiftTileProps {
    occurrence: ScheduledGiftOccurrence;
    gameElements: Map<string, GameElementDto>;
    onSelect: (occurence: ScheduledGiftOccurrence) => void;
}

export const GiftScheduleGiftTile = memo(function GiftScheduleGiftTile({
    occurrence,
    gameElements,
    onSelect,
}: GiftScheduleGiftTileProps) {
    const { gift, startDate, endDate } = occurrence;
    const hasMissing = gift.contents.some((id) => !gameElements.has(id));
    const republished =
        gift.availabilityDate && gift.availabilityDate < startDate;
    const dateRange = formatDateRange(startDate, endDate);

    const gameElementIds = useMemo(() => {
        const ids = [...gift.contents];
        if (gift.wrapperGameElementId) {
            ids.push(gift.wrapperGameElementId);
        }
        return ids;
    }, [gift]);

    return (
        <ElementTile
            gameElementIds={gameElementIds}
            onClick={() => onSelect?.(occurrence)}
            sx={
                republished
                    ? {
                          border: 'none',
                          outline: '2px dashed',
                          outlineColor: amber[600],
                      }
                    : undefined
            }
            bigSx={{
                bgcolor: hasMissing
                    ? 'warning.main'
                    : republished
                      ? alpha(amber[500], 0.35)
                      : 'grey.100',
            }}
            badge={
                hasMissing ? (
                    <Box
                        sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            color: 'common.white',
                            fontSize: 11,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid',
                            borderColor: 'background.paper',
                        }}
                    >
                        <Icon name="warning" />
                    </Box>
                ) : undefined
            }
            tooltip={
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {gift.name}
                    </Typography>
                    {dateRange && (
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', opacity: 0.85 }}
                        >
                            {dateRange}
                        </Typography>
                    )}
                    {hasMissing && (
                        <Alert color="warning" icon={<Icon name="warning" />}>
                            Gift has missing/unknown game element
                        </Alert>
                    )}
                </Box>
            }
        />
    );
});
